import { FC, Fragment, ReactElement, useEffect, useState } from 'react';
import { CheckboxInput, Typography } from 'melp-design/components';
import { Box, Stack, Tooltip } from '@mui/material';
import { ContentLanguage } from 'types/general';
import { useTranslation } from 'react-i18next';
import { HelpOutline } from '@mui/icons-material';
import { SystemColors } from 'melp-design/style';
import { formatLanguage } from 'utils/format';
import { usePrevious } from 'utils/hooks';

type Fields = Array<{
  id: string;
  language: Uppercase<ContentLanguage>;
  component: ReactElement;
}>;

interface Props {
  label?: string;
  info?: string;
  required?: boolean;
  disabled?: boolean;
  onIsSingleChange?: (isSingle: boolean) => void;
  fields: Fields | ((isSingle: boolean) => Fields);
}

export const MultiLanguageInputContainer: FC<Props> = ({
  label,
  info,
  required,
  disabled,
  onIsSingleChange,
  fields,
}) => {
  const { t, i18n } = useTranslation();

  const [isSingle, setIsSingle] = useState(false);

  const withSingleToggle = !Array.isArray(fields);
  const parsedFields = withSingleToggle ? fields(isSingle) : fields;
  const prevFieldsLength = usePrevious(parsedFields.length);

  useEffect(() => {
    if (prevFieldsLength !== null && prevFieldsLength !== parsedFields.length) {
      setIsSingle(false);
    }
  }, [parsedFields.length, prevFieldsLength]);

  return (
    <Stack gap={1}>
      {label ? (
        <Stack direction="row" alignItems="center">
          <Typography variant="p2">
            {[label, required ? '*' : null].filter(Boolean).join(' ')}
          </Typography>
          {info ? (
            <Tooltip title={info}>
              <HelpOutline sx={{ color: SystemColors.grey[55], height: 16 }} />
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}

      {withSingleToggle ? (
        <CheckboxInput
          label={t('common.single.label')}
          checked={isSingle}
          disabled={disabled}
          onChange={(checked) => {
            setIsSingle(checked);
            onIsSingleChange?.(checked);
          }}
        />
      ) : null}

      {withSingleToggle && isSingle ? (
        <Box>
          {parsedFields
            .filter((_, i) => i < 1)
            .map((field) => (
              <Fragment key={field.id}>{field.component}</Fragment>
            ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            rowGap: 1,
            columnGap: 2,
            alignItems: 'center',
          }}
        >
          {parsedFields.map((field) => (
            <Fragment key={field.id}>
              <Typography variant="p2" color="textSecondary">
                {formatLanguage(i18n.language, field.language)}
              </Typography>
              {field.component}
            </Fragment>
          ))}
        </Box>
      )}
    </Stack>
  );
};
